<template>

    <div class="mx-auto">
        <div class="grid rounded-xl overflow-hidden" :style="{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }">
            <div v-for="(style, index) in styles" :key="index" class="aspect-square relative overflow-hidden" :style="style.baseStyle">
                <div 
                    v-if="style.hasImage" 
                    class="absolute inset-0 bg-cover bg-center bg-no-repeat image-fade-in"
                    :style="style.imageStyle"
                ></div>
            </div>
        </div>

    </div>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// Grid configuration props
const props = defineProps({
    cols: {
        type: Number,
        default: 6
    },
    rows: {
        type: Number,
        default: 4
    }
})

const cols = computed(() => props.cols)
const rows = computed(() => props.rows)

const styles = ref([])
const cornerIndices = ref([])
const colorIndices = ref([])
const useImage = ref([])  // Track which elements use images
const imageIndices = ref([])  // Track which image index each element uses
let intervalId = null
let currentImageIndex = 0  // Track current rotation position
let lastChangedIndex = -1  // Track the last changed index to avoid changing it twice in a row


import bg1 from '@/images/photography/cropped/entegy-1.jpg'
import bg2 from '@/images/photography/cropped/entegy-2.jpg'
import bg3 from '@/images/photography/cropped/entegy-3.jpg'
import bg4 from '@/images/photography/cropped/entegy-4.jpg'
import bg5 from '@/images/photography/cropped/entegy-5.jpg'
import bg6 from '@/images/photography/cropped/entegy-6.jpg'
import bg7 from '@/images/photography/cropped/entegy-7.jpg'

const colors = ['#4f39f6', '#FF69B2', '#1e1e1e', '#4f39f6', '#FF69B2']
const backgroundImageUrls = [
    `url(${bg1})`,
    `url(${bg2})`,
    `url(${bg3})`,
    `url(${bg4})`,
    `url(${bg5})`,
    `url(${bg6})`,
    `url(${bg7})`,
]
const corners = [
    '70% 0 0 0',    // top-left
    '0 70% 0 0',    // top-right
    '0 0 70% 0',    // bottom-right
    '0 0 0 70%',    // bottom-left
    '50%'           // circle (no flat edges)
] 

function getFlatEdges(cornerIndex) {
    // Returns which edges are flat: ['left', 'right', 'top', 'bottom']
    // Corner 0 (top-left rounded): right and bottom are flat
    // Corner 1 (top-right rounded): left and bottom are flat
    // Corner 2 (bottom-right rounded): top and left are flat
    // Corner 3 (bottom-left rounded): top and right are flat
    // Corner 4 (circle): no flat edges (all rounded)
    const flatEdgeMap = {
        0: ['right', 'bottom'],
        1: ['left', 'bottom'],
        2: ['top', 'left'],
        3: ['top', 'right'],
        4: []  // circle has no flat edges
    }
    return flatEdgeMap[cornerIndex] || []
}

function getNeighborIndex(index, direction) {
    const col = index % cols.value
    const row = Math.floor(index / cols.value)
    
    if (direction === 'left' && col > 0) return index - 1
    if (direction === 'right' && col < cols.value - 1) return index + 1
    if (direction === 'top' && row > 0) return index - cols.value
    if (direction === 'bottom' && row < rows.value - 1) return index + cols.value
    return null
}

function hasFlatEdgesTouching(index1, index2) {
    const corner1 = cornerIndices.value[index1]
    const corner2 = cornerIndices.value[index2]
    
    const flatEdges1 = getFlatEdges(corner1)
    const flatEdges2 = getFlatEdges(corner2)
    
    // Determine relative position
    const col1 = index1 % cols.value
    const row1 = Math.floor(index1 / cols.value)
    const col2 = index2 % cols.value
    const row2 = Math.floor(index2 / cols.value)
    
    // Check if edges are touching
    if (col2 === col1 + 1 && row2 === row1) {
        // index2 is to the right of index1
        return flatEdges1.includes('right') && flatEdges2.includes('left')
    } else if (col2 === col1 - 1 && row2 === row1) {
        // index2 is to the left of index1
        return flatEdges1.includes('left') && flatEdges2.includes('right')
    } else if (col2 === col1 && row2 === row1 + 1) {
        // index2 is below index1
        return flatEdges1.includes('bottom') && flatEdges2.includes('top')
    } else if (col2 === col1 && row2 === row1 - 1) {
        // index2 is above index1
        return flatEdges1.includes('top') && flatEdges2.includes('bottom')
    }
    
    return false
}

function isHorizontalConnection(index1, index2) {
    const col1 = index1 % 4
    const row1 = Math.floor(index1 / 4)
    const col2 = index2 % 4
    const row2 = Math.floor(index2 / 4)
    
    // Check if horizontally adjacent (same row, adjacent columns)
    return row1 === row2 && (col2 === col1 + 1 || col2 === col1 - 1)
}

function buildStyle(index) {
    const baseStyle = {
        borderRadius: corners[cornerIndices.value[index]],
        transition: 'all 0.5s ease',
    }
    
    // Check for horizontal connection with neighbor
    const rightNeighbor = getNeighborIndex(index, 'right')
    const leftNeighbor = getNeighborIndex(index, 'left')
    let horizontalConnection = false
    let isLeftElement = false
    
    if (rightNeighbor !== null && hasFlatEdgesTouching(index, rightNeighbor) && isHorizontalConnection(index, rightNeighbor)) {
        horizontalConnection = true
        isLeftElement = true
    } else if (leftNeighbor !== null && hasFlatEdgesTouching(leftNeighbor, index) && isHorizontalConnection(leftNeighbor, index)) {
        horizontalConnection = true
        isLeftElement = false
    }
    
    if (useImage.value[index]) {
        // Use background image with a background color for smoother transitions
        const imageUrl = backgroundImageUrls[imageIndices.value[index] || 0]
        // Use a color that matches the color scheme (rotate through colors based on image index)
        const bgColorIndex = (imageIndices.value[index] || 0) % colors.length
        baseStyle.backgroundColor = colors[bgColorIndex]
        
        const imageStyle = {
            backgroundImage: imageUrl,
            backgroundRepeat: 'no-repeat',
        }
        
        if (horizontalConnection) {
            // Center image across both elements
            imageStyle.backgroundSize = '200% 100%'
            imageStyle.backgroundPosition = isLeftElement ? 'left center' : 'right center'
        } else {
            // Single element with image
            imageStyle.backgroundSize = 'cover'
            imageStyle.backgroundPosition = 'center'
        }
        
        return {
            baseStyle,
            hasImage: true,
            imageStyle
        }
    } else {
        // Use background color
        baseStyle.backgroundColor = colors[colorIndices.value[index]]
        return {
            baseStyle,
            hasImage: false,
            imageStyle: {}
        }
    }
}

function getAvailableColorIndex(index, excludeIndices = []) {
    // Get all neighbor colors (excluding connected ones)
    const directions = ['left', 'right', 'top', 'bottom']
    const neighborColors = new Set()
    
    directions.forEach(direction => {
        const neighborIndex = getNeighborIndex(index, direction)
        if (neighborIndex !== null && !hasFlatEdgesTouching(index, neighborIndex)) {
            // Neighbor exists but is NOT connected - exclude its color
            if (colorIndices.value[neighborIndex] !== undefined && !excludeIndices.includes(neighborIndex)) {
                neighborColors.add(colorIndices.value[neighborIndex])
            }
        }
    })
    
    // Find a color that's not used by non-connected neighbors
    const availableColors = colors
        .map((_, idx) => idx)
        .filter(idx => !neighborColors.has(idx))
    
    // If all colors are taken, use a random one (fallback)
    if (availableColors.length === 0) {
        return Math.floor(Math.random() * colors.length)
    }
    
    return availableColors[Math.floor(Math.random() * availableColors.length)]
}

function updateRandomSingleStyle() {
    // Get all available indices (excluding the last changed one)
    const availableIndices = []
    for (let i = 0; i < styles.value.length; i++) {
        if (i !== lastChangedIndex) {
            availableIndices.push(i)
        }
    }
    
    // If only one index is available (or all but one), use it; otherwise pick randomly
    const randomIndex = availableIndices.length > 0
        ? availableIndices[Math.floor(Math.random() * availableIndices.length)]
        : Math.floor(Math.random() * styles.value.length)
    
    lastChangedIndex = randomIndex
    
    // Update corner - ensure it changes to a different corner
    const currentCorner = cornerIndices.value[randomIndex]
    const availableCorners = corners
        .map((_, idx) => idx)
        .filter(idx => idx !== currentCorner)
    
    // If all corners are the same (shouldn't happen), fallback to random
    if (availableCorners.length > 0) {
        cornerIndices.value[randomIndex] = availableCorners[Math.floor(Math.random() * availableCorners.length)]
    } else {
        cornerIndices.value[randomIndex] = Math.floor(Math.random() * corners.length)
    }
    
    // 50% chance to use image instead of color, but only if no non-connected neighbors have images
    const wantsImage = Math.random() < 0.50
    
    // Check if any non-connected neighbor has an image
    const directions = ['left', 'right', 'top', 'bottom']
    let hasNonConnectedImageNeighbor = false
    
    directions.forEach(direction => {
        const neighborIndex = getNeighborIndex(randomIndex, direction)
        if (neighborIndex !== null && !hasFlatEdgesTouching(randomIndex, neighborIndex)) {
            // Neighbor exists but is NOT connected - check if it has an image
            if (useImage.value[neighborIndex]) {
                hasNonConnectedImageNeighbor = true
            }
        }
    })
    
    // Only use image if we want it AND no non-connected neighbors have images
    useImage.value[randomIndex] = wantsImage && !hasNonConnectedImageNeighbor
    
    if (useImage.value[randomIndex]) {
        // Assign next image in rotation
        imageIndices.value[randomIndex] = currentImageIndex
        currentImageIndex = (currentImageIndex + 1) % backgroundImageUrls.length
    } else {
        // Update color - ensure it's different from non-connected neighbors
        colorIndices.value[randomIndex] = getAvailableColorIndex(randomIndex)
    }
    
    // Sync colors/images with connected neighbors (flat edges touching)
    const updatedIndices = new Set([randomIndex])
    
    directions.forEach(direction => {
        const neighborIndex = getNeighborIndex(randomIndex, direction)
        if (neighborIndex !== null && hasFlatEdgesTouching(randomIndex, neighborIndex)) {
            // For horizontal connections, sync image usage and make it span
            if (isHorizontalConnection(randomIndex, neighborIndex)) {
                useImage.value[neighborIndex] = useImage.value[randomIndex]
                if (useImage.value[randomIndex]) {
                    // Share the same image index for horizontal connections
                    imageIndices.value[neighborIndex] = imageIndices.value[randomIndex]
                } else {
                    colorIndices.value[neighborIndex] = colorIndices.value[randomIndex]
                }
            } else {
                // For non-horizontal connections, sync color if not using images
                if (!useImage.value[randomIndex] && !useImage.value[neighborIndex]) {
                    colorIndices.value[neighborIndex] = colorIndices.value[randomIndex]
                }
            }
            updatedIndices.add(neighborIndex)
        }
    })
    
    // Update all affected style objects
    updatedIndices.forEach(idx => {
        styles.value[idx] = buildStyle(idx)
    })
}

// Initialize styles
const elementCount = computed(() => cols.value * rows.value)

// Initialize all corners, colors, and image usage
const directions = ['left', 'right', 'top', 'bottom']
for (let i = 0; i < elementCount.value; i++) {
    cornerIndices.value[i] = Math.floor(Math.random() * corners.length)
    
    // 25% chance to use image, but only if no non-connected neighbors have images
    const wantsImage = Math.random() < 0.25
    
    // Check if any non-connected neighbor has an image (only check already processed neighbors)
    let hasNonConnectedImageNeighbor = false
    
    directions.forEach(direction => {
        const neighborIndex = getNeighborIndex(i, direction)
        if (neighborIndex !== null && neighborIndex < i && !hasFlatEdgesTouching(i, neighborIndex)) {
            // Neighbor exists (already processed), is NOT connected - check if it has an image
            if (useImage.value[neighborIndex]) {
                hasNonConnectedImageNeighbor = true
            }
        }
    })
    
    // Only use image if we want it AND no non-connected neighbors have images
    useImage.value[i] = wantsImage && !hasNonConnectedImageNeighbor
    
    if (useImage.value[i]) {
        // Assign next image in rotation
        imageIndices.value[i] = currentImageIndex
        currentImageIndex = (currentImageIndex + 1) % backgroundImageUrls.length
    } else {
        // Get color that's different from non-connected neighbors
        colorIndices.value[i] = getAvailableColorIndex(i)
    }
}

// Sync colors/images for connected shapes
for (let i = 0; i < elementCount.value; i++) {
    directions.forEach(direction => {
        const neighborIndex = getNeighborIndex(i, direction)
        if (neighborIndex !== null && hasFlatEdgesTouching(i, neighborIndex)) {
            // For horizontal connections, sync image usage
            if (isHorizontalConnection(i, neighborIndex)) {
                if (neighborIndex < i) {
                    useImage.value[i] = useImage.value[neighborIndex]
                    if (useImage.value[i]) {
                        imageIndices.value[i] = imageIndices.value[neighborIndex]
                    } else {
                        colorIndices.value[i] = colorIndices.value[neighborIndex]
                    }
                } else {
                    useImage.value[neighborIndex] = useImage.value[i]
                    if (useImage.value[neighborIndex]) {
                        imageIndices.value[neighborIndex] = imageIndices.value[i]
                    } else {
                        colorIndices.value[neighborIndex] = colorIndices.value[i]
                    }
                }
            } else {
                // For non-horizontal connections, sync colors if not using images
                if (!useImage.value[i] && !useImage.value[neighborIndex]) {
                    if (neighborIndex < i) {
                        colorIndices.value[i] = colorIndices.value[neighborIndex]
                    } else {
                        colorIndices.value[neighborIndex] = colorIndices.value[i]
                    }
                }
            }
        }
    })
}

// Create style objects
styles.value = Array(elementCount.value).fill(null).map((_, index) => buildStyle(index))

function preloadImages() {
    // Extract URLs from backgroundImageUrls (format: 'url(...)')
    backgroundImageUrls.forEach(urlString => {
        const url = urlString.replace(/^url\((['"]?)(.*)\1\)$/, '$2')
        const img = new Image()
        img.src = url
    })
}

onMounted(() => {
    // Preload all images
    preloadImages()
    
    // Update a random style every 700ms
    intervalId = setInterval(updateRandomSingleStyle, 700);    
})

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId)
    }
})

</script>

<style scoped>
.image-fade-in {
    animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
</style>